import { jwtDecode } from "jwt-decode";

/** Claims we actually read off the Cognito id token. */
export interface IdTokenClaims {
  email?: string;
  name?: string;
  picture?: string;
  "cognito:groups"?: string[];
  [claim: string]: unknown;
}

const REDIRECT_KEY = "redirectAfterLogin";

/** The raw id token from localStorage, or null outside the browser. */
export function readIdToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem("idToken");
  } catch {
    return null;
  }
}

/**
 * Decode the stored Cognito id token.
 *
 * Replaces the hand-rolled base64url decoders that were copy-pasted across the
 * shop pages. Returns null when the token is absent or unparseable — callers
 * must treat that as "not signed in".
 */
export function decodeIdToken(token?: string | null): IdTokenClaims | null {
  const raw = token ?? readIdToken();
  if (!raw) return null;
  try {
    return jwtDecode<IdTokenClaims>(raw);
  } catch {
    return null;
  }
}

/** Remember where to send the user once they have signed in. */
export function setRedirectAfterLogin(path: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(REDIRECT_KEY, path);
  } catch {
    /* private window — the user just lands on their dashboard instead */
  }
}

/**
 * Read and clear the post-login return path.
 *
 * Only same-origin absolute paths are honoured — an attacker-controlled value
 * in storage must not become an open redirect. Consuming it here also stops a
 * stale path leaking into a later, unrelated login.
 */
export function consumeRedirectAfterLogin(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const target = window.localStorage.getItem(REDIRECT_KEY);
    window.localStorage.removeItem(REDIRECT_KEY);
    if (!target || !target.startsWith("/") || target.startsWith("//")) {
      return null;
    }
    return target;
  } catch {
    return null;
  }
}

/**
 * Authorization header for backend calls, or {} when signed out.
 *
 * Prefers the access token (meant for APIs); the backend verifies either.
 */
export function authHeaders(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const token =
      window.localStorage.getItem("accessToken") ||
      window.localStorage.getItem("idToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/** Whether the signed-in user is in the Cognito `admin` group (the backend checks again). */
export function isAdmin(): boolean {
  const groups = decodeIdToken()?.["cognito:groups"] ?? [];
  return groups.includes("admin");
}
